/**
 * Body-pinning page scroll lock, and the one honest reading of the page offset
 * while it is held.
 *
 * Pinning the body with `position: fixed` collapses the document to viewport
 * height, so the browser clamps the window offset to 0 and fires a scroll event.
 * Every `window.scrollY` read taken while an overlay is open therefore reports
 * the top of the page — which is why an edit started from the post-options menu
 * (open at that exact moment) carried offset 0 and returned the user to the top
 * of the feed. Readers use `getPageScrollY()` instead.
 */

/** Nesting depth, so an inner overlay closing does not release the outer lock. */
let lockDepth = 0;

/** The offset the page is pinned at, or null when nothing is pinned. */
let lockedScrollY: number | null = null;

export const lockPageScroll = (): void => {
  lockDepth += 1;
  if (lockDepth > 1) return;
  lockedScrollY = window.scrollY;
  const { body } = document;
  body.style.position = 'fixed';
  body.style.top = `-${lockedScrollY}px`;
  body.style.width = '100%';
};

export const unlockPageScroll = (): void => {
  if (lockDepth === 0) return;
  lockDepth -= 1;
  if (lockDepth > 0) return;
  const offset = lockedScrollY ?? 0;
  lockedScrollY = null;
  const { body } = document;
  body.style.position = '';
  body.style.top = '';
  body.style.width = '';
  window.scrollTo(0, offset);
};

/** Where the page really is: the pinned offset while locked, otherwise the live one. */
export const getPageScrollY = (): number =>
  lockedScrollY ?? (typeof window === 'undefined' ? 0 : window.scrollY);
